import { type ComponentType, useRef, useState } from "react";

import { type ParseMealView, api } from "../lib/api";
import { useLang } from "../lib/i18n";
import { mealsCopy } from "../lib/i18n/meals";
import { fileToCompressedDataUrl } from "../lib/image";
import { useProfile } from "../lib/profile-context";
import { ScreenShell } from "./screen-shell";
import {
  IconCamera,
  IconGallery,
  IconKeyboard,
  IconPlate,
  IconSun,
  IconTopDown,
} from "./ui/icon";
import { H1, TopBar } from "./ui/primitives";

type Props = {
  onParsed: (parsed: ParseMealView) => void;
  onCancel: () => void;
};

// Photo tips strip per B_Photo design (handoff screens-b.jsx). SVG glyphs
// replace the legacy 💡 📐 🥑 emoji — emoji render inconsistently across
// iOS/Android/desktop fonts and look childish next to the rest of the
// stroke-1.8 icon system. Labels still come from the localized catalog.
type TipIcon = ComponentType<{ size?: number }>;
const TIP_ICONS: TipIcon[] = [IconSun, IconTopDown, IconPlate];

export function MealPhotoScreen({ onParsed, onCancel }: Props) {
  const { lang } = useLang();
  const t = mealsCopy[lang];
  // The active profile decides whether the photo is parsed in adult or
  // child-portion mode (ADR-0018). Threading the child id to the API
  // switches the server-side prompt and the recent-meals few-shot to
  // child-only history, eliminating the adult-portion prior bug.
  const { active } = useProfile();
  const TIPS = TIP_ICONS.map((Icon, i) => ({ Icon, label: t.photoTips[i] }));
  // Two file inputs so the camera button opens the camera directly (capture
  // attribute) while the gallery button uses the standard picker. Without
  // this, mobile Safari always shows the picker chooser.
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File | null) {
    if (!file) return;
    setError(null);
    setSubmitting(true);
    try {
      // 1536px long edge — food texture/sauce detail helps recognition.
      const dataUrl = await fileToCompressedDataUrl(file, { maxEdge: 1536 });
      setPreview(dataUrl);
      const parsed = await api.parseMealPhoto(
        dataUrl,
        active.kind === "child" ? active.childId : undefined,
      );
      if (parsed.items.length === 0) {
        setError(t.photoNoFood);
        return;
      }
      onParsed(parsed);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.photoAnalyzeError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ScreenShell hideHeader>
      <TopBar back={t.photoBack} title={t.photoTitle} onBack={onCancel} />

      <div style={{ marginTop: 18 }}>
        <H1>{t.photoH1}</H1>
        <p className="text-[14px] leading-snug" style={{ marginTop: 10, color: "var(--ink-2)" }}>
          {t.photoLead}
        </p>
      </div>

      {/* Viewfinder — dark surface with corner brackets and a status line. */}
      <div
        className="relative"
        style={{
          marginTop: 24,
          height: 340,
          borderRadius: 18,
          background: "var(--ink)",
          overflow: "hidden",
          backgroundImage: preview
            ? undefined
            : "repeating-linear-gradient(45deg, rgba(255,255,255,0.03) 0 1px, transparent 1px 12px)",
        }}
      >
        {preview ? (
          <img
            src={preview}
            alt={t.photoPreviewAlt}
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : null}

        {/* Four corner brackets — accent-colored, 28px L-shapes. */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 20,
            left: 20,
            width: 28,
            height: 28,
            borderTop: "2px solid var(--accent)",
            borderLeft: "2px solid var(--accent)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 20,
            right: 20,
            width: 28,
            height: 28,
            borderTop: "2px solid var(--accent)",
            borderRight: "2px solid var(--accent)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: 20,
            left: 20,
            width: 28,
            height: 28,
            borderBottom: "2px solid var(--accent)",
            borderLeft: "2px solid var(--accent)",
          }}
        />
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            bottom: 20,
            right: 20,
            width: 28,
            height: 28,
            borderBottom: "2px solid var(--accent)",
            borderRight: "2px solid var(--accent)",
          }}
        />

        {/* Status header. Offset past the top-left corner bracket (left=20,
            28 wide + 8 gap) so the mono label is not bisected by the L. */}
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 56,
            fontFamily: "var(--font-mono)",
            fontSize: 9,
            color: "var(--accent)",
            letterSpacing: "0.1em",
          }}
        >
          {submitting ? t.photoStatusAnalyzing : t.photoStatusReady}
        </div>

        {/* Centered plate glyph when no preview. */}
        {!preview ? (
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%,-50%)",
              fontFamily: "var(--font-display)",
              fontSize: 60,
              fontWeight: 800,
              color: "rgba(255,255,255,0.18)",
              letterSpacing: "-0.05em",
            }}
          >
            🍽
          </div>
        ) : null}

      </div>

      {/* Frame caption — lives OUTSIDE the viewfinder so it does not get
          chopped by the bottom-left / bottom-right corner brackets. */}
      <div
        style={{
          marginTop: 8,
          fontFamily: "var(--font-mono)",
          fontSize: 10,
          color: "var(--muted)",
          letterSpacing: "0.06em",
          textAlign: "center",
        }}
      >
        {t.photoFrameHint}
      </div>

      {/* Tips strip — three pill cards. SVG glyph + label, replaces the
          legacy emoji prefix on each chip. */}
      <div className="flex gap-2" style={{ marginTop: 16 }}>
        {TIPS.map(({ Icon, label }) => (
          <span
            key={label}
            className="flex-1 flex items-center justify-center gap-1.5"
            style={{
              padding: "10px 12px",
              borderRadius: 10,
              fontSize: 11,
              fontWeight: 600,
              background: "var(--surface)",
              color: "var(--ink-2)",
              boxShadow: "0 0 0 1px var(--nav-border)",
            }}
          >
            <Icon size={14} />
            {label}
          </span>
        ))}
      </div>

      {error ? (
        <p
          className="text-[12px] px-3 py-2 rounded-md"
          style={{ background: "var(--bad-bg)", color: "var(--bad-fg)", marginTop: 16 }}
        >
          {error}
        </p>
      ) : null}

      {/* Hidden file inputs — one for camera, one for gallery. */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          handleFile(file);
          e.target.value = "";
        }}
      />
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0] ?? null;
          handleFile(file);
          e.target.value = "";
        }}
      />

      {/* Action row — manual entry · shutter · gallery. */}
      <div
        className="flex items-center justify-center gap-2.5"
        style={{ marginTop: 24, marginBottom: 8 }}
      >
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          aria-label={t.photoManualAria}
          className="flex items-center justify-center disabled:opacity-50"
          style={{
            width: 54,
            height: 54,
            borderRadius: 99,
            background: "var(--surface)",
            color: "var(--ink)",
            border: "none",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <IconKeyboard size={22} />
        </button>
        <button
          type="button"
          onClick={() => cameraInputRef.current?.click()}
          disabled={submitting}
          aria-label={submitting ? t.photoShutterBusyAria : t.photoShutterAria}
          className="flex items-center justify-center disabled:opacity-50"
          style={{
            width: 76,
            height: 76,
            borderRadius: 99,
            background: "var(--accent)",
            color: "var(--accent-ink)",
            border: "none",
            boxShadow: "0 0 0 6px rgba(255,90,31,0.15), var(--shadow-cta)",
          }}
        >
          <IconCamera size={28} />
        </button>
        <button
          type="button"
          onClick={() => galleryInputRef.current?.click()}
          disabled={submitting}
          aria-label={t.photoGalleryAria}
          className="flex items-center justify-center disabled:opacity-50"
          style={{
            width: 54,
            height: 54,
            borderRadius: 99,
            background: "var(--surface)",
            color: "var(--ink)",
            border: "none",
            boxShadow: "0 0 0 1px var(--nav-border)",
          }}
        >
          <IconGallery size={22} />
        </button>
      </div>

      {submitting ? (
        <p
          className="text-center text-[11px] uppercase"
          style={{
            fontFamily: "var(--font-mono)",
            color: "var(--muted)",
            letterSpacing: "0.1em",
            marginTop: 4,
          }}
        >
          {t.photoBusyCaption}
        </p>
      ) : null}
    </ScreenShell>
  );
}
